import React, { Fragment, memo, useMemo, type ReactNode } from 'react';
import castArray from 'lodash/castArray';
import { graphql, useFragment } from 'react-relay';
import { xcss, type XCSS } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import type { issueRow_nativeIssueTable_IssueRowWithFragments_fieldSetsForIssueSearchView$key } from '@atlassian/jira-relay/src/__generated__/issueRow_nativeIssueTable_IssueRowWithFragments_fieldSetsForIssueSearchView.graphql';
import type { issueRow_nativeIssueTable_IssueRowWithFragments_issue$key } from '@atlassian/jira-relay/src/__generated__/issueRow_nativeIssueTable_IssueRowWithFragments_issue.graphql';
import { toProjectKey } from '@atlassian/jira-shared-types';
import { COLUMN_ID_MEATBALL_MENU, KNOWN_COLUMN_TYPES } from '../../../common/constants';
import type { Column, TableInjectedCellProps } from '../../../common/types';
import {
	AffectedServicesCell,
	CmdbObjectsCell,
	ComponentsCell,
	DateCell,
	EpicLinkCell,
	FallbackCell,
	IssueKeyCell,
	IssueOperations,
	IssueStatusCategoryCell,
	IssueStatusCell,
	IssueSummaryCell,
	IssueTypeCell,
	LabelsCell,
	ParentCell,
	PlainTextCell,
	PriorityCell,
	ProjectCell,
	ResolutionCell,
	SprintCell,
	UserCell,
	VersionsCell,
	RichTextCell,
	RequestTypeCell,
	IssueLinksCell,
} from '../../../common/ui/issue-cells';
import { TableCell, type Props as TableCellProps } from '../../../common/ui/table-cell';
import { getProjectKeyFromIssueKey, isNonNullish } from '../../../common/utils';
import IssueRowCell from './cell';

export type Props = {
	columns: Column[];
	tableInjectedProps: TableInjectedCellProps;
	issue: issueRow_nativeIssueTable_IssueRowWithFragments_issue$key;
	hasInlineEditing: boolean;
	hasFixedTableLayout: boolean;
	cellBorder?: TableCellProps['border'];
};

export const IssueRowWithFragments = ({
	issue,
	columns,
	tableInjectedProps,
	hasInlineEditing,
	hasFixedTableLayout,
	cellBorder,
}: Props) => {
	const issueData = useFragment<issueRow_nativeIssueTable_IssueRowWithFragments_issue$key>(
		graphql`
			fragment issueRow_nativeIssueTable_IssueRowWithFragments_issue on JiraIssue
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean!" }
				isChildIssueTotalCountEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-child-issues-total-count-enabled.relayprovider"
				}
			) {
				key
				issueId
				fieldSetsById(fieldSetIds: $fieldSetIds, first: $amountOfColumns)
					@required(action: THROW)
					@include(if: $shouldQueryFieldSetsById) {
					...issueRow_nativeIssueTable_IssueRowWithFragments_fieldSetsForIssueSearchView
						@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
				}
				fieldSetsForIssueSearchView(
					namespace: $namespace
					viewId: $viewId
					first: $amountOfColumns
					filterId: $filterId
				) @required(action: THROW) @skip(if: $shouldQueryFieldSetsById) {
					...issueRow_nativeIssueTable_IssueRowWithFragments_fieldSetsForIssueSearchView
						@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
				}
				# eslint-disable-next-line relay/unused-fields
				childIssues @include(if: $isChildIssueTotalCountEnabled) {
					... on JiraChildIssuesWithinLimit {
						issues(first: 10, activeProjectsOnly: true) {
							totalCount
						}
					}
				}
			}
		`,
		issue,
	);

	const fieldSets =
		useFragment<issueRow_nativeIssueTable_IssueRowWithFragments_fieldSetsForIssueSearchView$key>(
			graphql`
				fragment issueRow_nativeIssueTable_IssueRowWithFragments_fieldSetsForIssueSearchView on JiraIssueFieldSetConnection
				@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
					edges {
						node {
							type
							fieldSetId
							fields {
								... on JiraIssueFieldConnection {
									edges {
										node {
											__typename
											fieldId
											type
											# eslint-disable-next-line relay/unused-fields
											isEditableInIssueView @include(if: $isEditableReadsEnabled)
											...affectedServices_nativeIssueTable_AffectedServicesCell
											...cmdbObjects_nativeIssueTable_CmdbObjectsCell
											...components_nativeIssueTable_ComponentsCell
											...date_nativeIssueTable_DateCell
												@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											...epicLink_nativeIssueTable_EpicLinkCell
											...fallback_nativeIssueTable_FallbackCell
											...issueKey_nativeIssueTable_IssueKeyCell
											...issueStatusCategory_nativeIssueTable_IssueStatusCategoryCell
											...issueStatus_nativeIssueTable_IssueStatusCell
												@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											...issueSummary_nativeIssueTable_IssueSummaryCell
												@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											...labels_nativeIssueTable_LabelsCell
												@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											...issueType_nativeIssueTable_IssueTypeCell
											...parent_nativeIssueTable_ParentCell
											...priority_nativeIssueTable_PriorityCell
												@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											...project_nativeIssueTable_ProjectCell
											...resolution_nativeIssueTable_ResolutionCell
											...sprint_nativeIssueTable_SprintCell
												@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											...user_nativeIssueTable_UserCell_fieldRef
												@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
											...versions_nativeIssueTable_VersionsCell
											...plainText_nativeIssueTable_PlainTextCell
											...richText_nativeIssueTable_RichTextCell
											...requestType_nativeIssueTable_RequestTypeCell
											...issueLinks_nativeIssueTable_IssueLinksCell
										}
									}
								}
							}
						}
					}
				}
			`,
			issueData.fieldSetsById ?? issueData.fieldSetsForIssueSearchView ?? null,
		);

	const columnConfigByIdMap = useMemo(() => {
		const result = new Map<string | null, Column>();
		columns.forEach((columnConfig) => {
			result.set(columnConfig.id, columnConfig);
		});
		return result;
	}, [columns]);

	const fieldElementByIdMap = useMemo(() => {
		// Map all issue fields into their respective cell type keyed by fieldSetId. This will be used as a lookup when
		// mapping table columns in the row.
		const result = new Map<
			string,
			{
				cellElement: ReactNode;
				spacing?: XCSS;
			}
		>();
		const projectKey = getProjectKeyFromIssueKey(issueData.key);

		fieldSets?.edges
			?.map((edge) => edge?.node)
			.filter(isNonNullish)
			.forEach((fieldSet) => {
				// @ts-expect-error - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string | null'.
				const columnConfig = columnConfigByIdMap.get(fieldSet.fieldSetId);
				if (!fieldSet.fieldSetId || !columnConfig) {
					return;
				}

				// Handle Collapsed Fields on a single issue (rare) by simply rendering a node for each field.
				const fieldElements: ReactNode[] = [];
				// Allow td spacing to be configured for the given field type
				let spacing: XCSS | undefined;
				fieldSet.fields?.edges?.forEach((fieldEdge) => {
					switch (fieldEdge?.node?.__typename) {
						case 'JiraAffectedServicesField':
							fieldElements.push(
								<AffectedServicesCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraCMDBField':
							fieldElements.push(
								<CmdbObjectsCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);

							break;
						case 'JiraComponentsField':
							fieldElements.push(
								<ComponentsCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									jqlTerm={columnConfig.jqlTerm}
									projectKey={projectKey}
								/>,
							);
							break;
						case 'JiraDatePickerField':
							fieldElements.push(
								<DateCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							if (fieldEdge.node.fieldId === KNOWN_COLUMN_TYPES.DUE_DATE) {
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraDateTimePickerField':
							fieldElements.push(
								<DateCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isRequired={
										fieldEdge.node.type === KNOWN_COLUMN_TYPES.CREATED ||
										fieldEdge.node.type === KNOWN_COLUMN_TYPES.UPDATED
									}
								/>,
							);
							break;
						case 'JiraEpicLinkField':
							fieldElements.push(
								<EpicLinkCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);

							break;
						case 'JiraFallbackField':
							fieldElements.push(
								<FallbackCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							if (ff('nin-resizable-columns_8z4eo')) {
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraIssueTypeField':
							fieldElements.push(
								<IssueTypeCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraLabelsField':
							fieldElements.push(
								<LabelsCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									jqlTerm={columnConfig.jqlTerm}
								/>,
							);
							if (fieldEdge.node.fieldId === KNOWN_COLUMN_TYPES.LABELS) {
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraMultipleVersionPickerField':
							fieldElements.push(
								<VersionsCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									jqlTerm={columnConfig.jqlTerm}
									projectKey={projectKey}
								/>,
							);
							break;
						case 'JiraParentIssueField':
							fieldElements.push(
								<ParentCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraPriorityField':
							fieldElements.push(
								<PriorityCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);

							spacing = noSpacingStyles;
							break;
						case 'JiraProjectField':
							fieldElements.push(
								<ProjectCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									isRequired={fieldEdge.node.type === KNOWN_COLUMN_TYPES.PROJECT}
								/>,
							);
							break;
						case 'JiraResolutionField':
							fieldElements.push(
								<ResolutionCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraRichTextField':
							fieldElements.push(
								<RichTextCell
									issueKey={issueData.key}
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
								/>,
							);
							if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')) {
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraSingleLineTextField':
							if (fieldEdge.node.type === KNOWN_COLUMN_TYPES.ISSUE_KEY) {
								fieldElements.push(
									<IssueKeyCell
										key={fieldEdge.node.fieldId}
										fieldRef={fieldEdge.node}
										onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
									/>,
								);
								if (ff('nin-resizable-columns_8z4eo')) {
									spacing = noSpacingStyles;
								}
							} else if (fieldEdge.node.type === KNOWN_COLUMN_TYPES.SUMMARY) {
								fieldElements.push(
									<IssueSummaryCell
										key={fieldEdge.node.fieldId}
										fieldRef={fieldEdge.node}
										issueKey={issueData.key}
										onIssueLinkSelected={tableInjectedProps.onIssueLinkSelected}
									/>,
								);
								spacing = noSpacingStyles;
							} else {
								fieldElements.push(
									<PlainTextCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
								);

								if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')) {
									spacing = noSpacingStyles;
								}
							}
							break;
						case 'JiraSingleSelectUserPickerField':
							fieldElements.push(
								<UserCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									projectKey={projectKey}
								/>,
							);
							if (fieldEdge.node.fieldId === 'assignee' || fieldEdge.node.fieldId === 'reporter') {
								spacing = noSpacingStyles;
							}
							break;
						case 'JiraSprintField':
							fieldElements.push(
								<SprintCell
									key={fieldEdge.node.fieldId}
									projectKey={toProjectKey(projectKey)}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									jqlTerm={columnConfig.jqlTerm}
								/>,
							);
							spacing = noSpacingStyles;
							break;
						case 'JiraStatusCategoryField':
							fieldElements.push(
								<IssueStatusCategoryCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraStatusField':
							fieldElements.push(
								<IssueStatusCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									hasInlineEditing={hasInlineEditing}
									issueKey={issueData.key}
									issueId={issueData.issueId}
									onUpdateCache={tableInjectedProps.onUpdateCache}
								/>,
							);
							break;
						case 'JiraServiceManagementRequestTypeField':
							fieldElements.push(
								<RequestTypeCell key={fieldEdge.node.fieldId} fieldRef={fieldEdge.node} />,
							);
							break;
						case 'JiraIssueLinkField':
							fieldElements.push(
								<IssueLinksCell
									key={fieldEdge.node.fieldId}
									fieldRef={fieldEdge.node}
									issueSearchBaseUrl={tableInjectedProps.issueSearchBaseUrl}
									issueKey={issueData.key}
								/>,
							);
							break;
						default:
							// Unrecognised field type encountered. Render empty cell.
							break;
					}
				});
				if (fieldElements.length) {
					result.set(fieldSet.fieldSetId, {
						cellElement: fieldElements,
						spacing,
					});
				}
			});

		// Always include the Issue Operations meatball menu cell.
		result.set(COLUMN_ID_MEATBALL_MENU, {
			cellElement: (
				<IssueOperations
					key={COLUMN_ID_MEATBALL_MENU}
					issueId={Number(issueData.issueId)}
					issueKey={issueData.key}
					onIssueRefetch={tableInjectedProps.onIssueRefetch}
					onIssueDelete={tableInjectedProps.onIssueDelete}
				/>
			),
		});

		return result;
	}, [
		columnConfigByIdMap,
		fieldSets?.edges,
		hasInlineEditing,
		issueData,
		tableInjectedProps.issueSearchBaseUrl,
		tableInjectedProps.onIssueRefetch,
		tableInjectedProps.onIssueDelete,
		tableInjectedProps.onIssueLinkSelected,
		tableInjectedProps.onUpdateCache,
	]);

	const cellBorderWithNoInlineBordersStyles = useMemo(() => {
		if (cellBorder === undefined) {
			return noInlineBordersStyles;
		}
		return [...castArray(cellBorder), noInlineBordersStyles];
	}, [cellBorder]);

	return (
		<>
			{columns.map(({ id, type, width, shouldResizeWithTable }) => {
				const field = fieldElementByIdMap.get(id);
				return (
					<Fragment key={id}>
						{
							// TODO: EM-7165 – Update the extra filler cell logic when refactoring the column picker column
							// Empty cell to fill remainig space if configured columns are not wide enough to fill the table
							ff('nin-resizable-columns_8z4eo') &&
								hasFixedTableLayout &&
								id === COLUMN_ID_MEATBALL_MENU && (
									<TableCell border={cellBorderWithNoInlineBordersStyles} aria-hidden />
								)
						}

						<IssueRowCell
							type={type}
							cellElement={field?.cellElement}
							spacing={field?.spacing}
							columnWidth={width}
							isFlexWidth={!!shouldResizeWithTable}
							border={cellBorder}
						/>
					</Fragment>
				);
			})}
		</>
	);
};

const noSpacingStyles = xcss({
	padding: 'space.0',
});

const noInlineBordersStyles = xcss({
	borderLeftWidth: '0',
	borderRightWidth: '0',
});

export const IssueRow = memo(IssueRowWithFragments);
